import React, { useMemo, useRef } from 'react';
import Alert from 'react-s-alert';
import { useViewerControl } from 'libs/rendering/gltf/hooks';
import { useContextManager } from 'apps/context/hooks';


import Icon from 'components/Icon';
import Tooltip from 'components/Tooltip';

import {
  OptionsGroup,
  OptionIcon,
  MouseIcon,
} from 'libs/rendering/gltf/components/measurements/measurements.styled';
import {
  MeasureAxisIcon,
  MeasureCenterIcon,
  DeleteIcon,
  OffsetIcon,
  RotateIcon,
  Select3DIcon,
} from 'icons';
import { Button } from 'components';
import { themes } from 'styles/themes/wikifactoryNew';
import { renderCustomIcon } from 'utils/jsxUtils';
import { getTargetMeasureIcon, getMeasurementTypeLabel, getMeasurementBody } from './utils';

import MeasureItem  from './measureItem';

const tooltip = (
  <Tooltip delayShow={500} place='bottom' id='tempMeasureTooltip' />
);

export const useMeasurementMenu = () => {
  const viewerControl = useViewerControl();
  const contextManager = useContextManager();

  const { viewer, cadFile } = viewerControl;
  if (!viewer) return null;

  const { selectionManager, settings, module, notePad } = viewer || {};
  if (!selectionManager) return null;
  if (!module) return null;

  const selModeEnum = module.SelectionMode;
  const isAdding =
    selectionManager.GetSelectionMode() === selModeEnum.SelectionModeMeasurements;

  const { pmi } = cadFile || {};
  const currCamera = settings?.CurrentCamera();
  // - itemId is cadId of the thing we're looking at
  let itemId = currCamera?.GetItem()?.uuid;

  const brepCount = pmi?.BrepCount();

  const measurement = pmi?.GetStartedMeasurement();
  const isTemporaryMeasurement = measurement?.isTemp;

  const { MeasurementType } = module || {};
  const ptTypeEnum = module.PointTargetType;
  const highlightsText = selectionManager?.MkpHighlightCount() > 0;
  const lastHigh =
    isAdding && !highlightsText ? selectionManager.GetLastHighlight() : null;

  const waitingForItem = !measurement?.finished;
  // const stickyOptions =
  //   isTemporaryMeasurement && brepCount > 0 && waitingForItem;
  // const positionOptions =
  //   lastSelMkp || // user selects an existent measurement
  //   measurement?.finished; // from-to measurement finished (has both start and end)

  const noBrepMsg = () => {
    return (
      <>
        <div className='flex items-center'>
          <div className='mr-2'>
            <Icon name='mesh' size='default' viewBox='0 0 40 40' />
          </div>
          Mesh-only model
        </div>
        <div className='flex items-center'>
          <div className='mr-2'>
            <Icon name='info' size='default' viewBox='0 0 24 24' />
          </div>
          No item dimensions available
        </div>
      </>
    );
  };

  const msrTypeLabel = measurement
    ? getMeasurementTypeLabel(measurement.type, MeasurementType)
    : null;

  let infoMsg = waitingForItem ? 'click model to start measuring' : null;
  let infoIcon = waitingForItem ? 'select_3d' : null;

  const getInfoMsg = () => {
    if (!waitingForItem) {
      return null;
    }

    if (measurement?.finished) {
      return 'ready';
    }

    if (msrTypeLabel) {
      return `${msrTypeLabel.toLowerCase()}`;
    }

    return null;
  };

  if (pmi.IsFromToMode()) {
    if (!measurement?.finished && lastHigh) {
      infoIcon = 'mouse_left';
      infoMsg = ` ${
        measurement && !measurement.isAuxFromToTemp ? 'end' : 'start'
      } point measure`;
    } else if (measurement?.finished && measurement?.isTemp) {
      infoIcon = 'send';
      infoMsg = ` click send to finish measurement`;
    }
  } else if (measurement) {
    if (!measurement?.finished) {
      infoMsg = getInfoMsg();
    } else if (measurement?.finished && measurement?.isTemp) {
      infoIcon = 'send';
      infoMsg = ` click send to finish measurement`;
    }

  }

  // permanent mouse down for tect offset
  // const textOffsetInterval = useRef<NodeJS.Timer>();
  // const textOffsetIntervalMs = 100;
  const isEmpty = brepCount === 0 && !pmi.IsFromToMode();

  const fromToElement = useMemo(() => {
    let startElement = getTargetMeasureIcon(null, ptTypeEnum);
    let endElement = getTargetMeasureIcon(null, ptTypeEnum);

    if (measurement) {
      startElement = getTargetMeasureIcon(measurement.startTgt, ptTypeEnum);

      endElement = measurement?.finished
        ? getTargetMeasureIcon(measurement.endTgt, ptTypeEnum)
        : getTargetMeasureIcon(measurement.endTgt, ptTypeEnum, true);
    } else if (lastHigh) {
      startElement = getTargetMeasureIcon(lastHigh, ptTypeEnum, true);
    }

    return (
      <div className='h-full flex items-center text-center'>
        From{startElement}to{endElement}
      </div>
    );
  }, [measurement, lastHigh, ptTypeEnum]);

  const handleClickPointToPoint = () => {
    pmi.SetFromToMode(true);
    // delete temp measurement
    pmi.AbortStartedMeasurement();
    pmi.reportWasmChange();
    notePad.reportWasmChange();
  };

  const handleClickDimension = () => {
    pmi.SetFromToMode(false);
    // delete temp measurement
    pmi.AbortStartedMeasurement();
    pmi.reportWasmChange();
    notePad.reportWasmChange();
  };

  const iconMap: Record<string, React.ReactNode> = {
    select_3d: <Select3DIcon />,
  };

  // measurement edition options
  const descriptionInfo = infoIcon && (
    <div className='flex' style={{ color: themes.color.wfBlue }}>
      {iconMap[infoIcon] || (
        <MouseIcon>
          <Icon name={infoIcon} size='small' viewBox='0 0 40 40' />
        </MouseIcon>
      )}
      <p className='ml-1 text-sm'>{infoMsg}</p>
    </div>
  );

  // const disableFromSelect = !isTemporaryMeasurement && !measurement?.isFromTo;
  // const disableDimension = !isTemporaryMeasurement && measurement?.isFromTo;
  const isFromToModeActive = pmi.IsFromToMode();
  const isActiveDimension = !pmi.IsFromToMode();

  // const shouldShowStickOptions = isFromToModeActive && stickyOptions;
  // const stickyOptionGroup = shouldShowStickOptions && (
  //   <OptionsGroup atRight style={{ paddingLeft: 6 }}>
  //     <OptionIcon
  //       hide={!stickyOptions}
  //       active={pmi.stickToCenter}
  //       onClick={() => {
  //         pmi.stickToCenter = !pmi.stickToCenter;
  //       }}
  //     >
  //       <MeasureCenterIcon
  //         data-for='tempMeasureTooltip'
  //         data-tip='Stick to center'
  //       />
  //     </OptionIcon>
  //     <OptionIcon
  //       hide={!stickyOptions}
  //       active={pmi.stickToAxis}
  //       onClick={() => {
  //         pmi.stickToAxis = !pmi.stickToAxis;
  //       }}
  //     >
  //       <MeasureAxisIcon
  //         data-for='tempMeasureTooltip'
  //         data-tip='Stick to axis'
  //       />
  //     </OptionIcon>
  //     {tooltip}
  //   </OptionsGroup>
  // );

  // const rotateElement = (
  //   <OptionsGroup>
  //     <OptionIcon hide={!positionOptions} alwaysActive>
  //       <Icon
  //         name='caret_left'
  //         size='small'
  //         viewBox='0 0 24 24'
  //         data-for='tempMeasureTooltip'
  //         data-tip='Rotate text'
  //         onClick={() => {
  //           const mkp = lastSelMkp ? lastSelMkp._wasm : null;
  //           settings?.UpdateMkpItemPosition(mkp, 1, 1);
  //         }}
  //       />
  //     </OptionIcon>
  //     {tooltip}
  //     <OptionIcon hide={!positionOptions} tight disabled>
  //       <RotateIcon />
  //     </OptionIcon>
  //     <OptionIcon hide={!positionOptions} alwaysActive>
  //       <Icon
  //         name='caret_right'
  //         size='small'
  //         viewBox='0 0 24 24'
  //         data-for='tempMeasureTooltip'
  //         data-tip='Rotate text'
  //         onClick={() => {
  //           const mkp = lastSelMkp ? lastSelMkp._wasm : null;
  //           settings?.UpdateMkpItemPosition(mkp, 1, -1);
  //         }}
  //       />
  //     </OptionIcon>
  //   </OptionsGroup>
  // );

  // const offsetElement = (
  //   <OptionsGroup>
  //     <OptionIcon hide={!positionOptions} alwaysActive>
  //       <Icon
  //         name='caret_left'
  //         size='small'
  //         viewBox='0 0 24 24'
  //         data-for='tempMeasureTooltip'
  //         data-tip='Offset text'
  //         onClick={() => {
  //           const mkp = lastSelMkp ? lastSelMkp._wasm : null;
  //           settings?.UpdateMkpItemPosition(mkp, 0.95, 0);
  //         }}
  //         // eslint-disable-next-line no-return-assign
  //         onMouseDown={() =>
  //           (textOffsetInterval.current = setInterval(() => {
  //             const mkp = lastSelMkp ? lastSelMkp._wasm : null;
  //             settings?.UpdateMkpItemPosition(mkp, 0.95, 0);
  //           }, textOffsetIntervalMs))
  //         }
  //         onMouseUp={() => {
  //           clearInterval(textOffsetInterval.current);
  //         }}
  //       />
  //     </OptionIcon>
  //     {tooltip}
  //     <OptionIcon hide={!positionOptions} tight disabled>
  //       <OffsetIcon />
  //     </OptionIcon>
  //     <OptionIcon hide={!positionOptions} alwaysActive>
  //       <Icon
  //         name='caret_right'
  //         size='small'
  //         viewBox='0 0 24 24'
  //         data-for='tempMeasureTooltip'
  //         data-tip='Offset text'
  //         onClick={() => {
  //           const mkp = lastSelMkp ? lastSelMkp._wasm : null;
  //           settings?.UpdateMkpItemPosition(mkp, 1.05, 0);
  //         }}
  //         // eslint-disable-next-line no-return-assign
  //         onMouseDown={() =>
  //           (textOffsetInterval.current = setInterval(() => {
  //             const mkp = lastSelMkp ? lastSelMkp._wasm : null;
  //             settings?.UpdateMkpItemPosition(mkp, 1.05, 0);
  //           }, textOffsetIntervalMs))
  //         }
  //         onMouseUp={() => {
  //           clearInterval(textOffsetInterval.current);
  //         }}
  //       />
  //     </OptionIcon>
  //   </OptionsGroup>
  // );

  const deleteOrDiscardBtn = (
    <OptionIcon atRight hide={waitingForItem}>
      <Button
        renderIcon={(hover: boolean) =>
          renderCustomIcon(DeleteIcon, {
            isHover: hover,
            hoverFillColor: themes.color.wfRed,
          })
        }
        variant='tertiary'
        size='xsmall'
        appearance='plain'
        onClick={() => {
          if (measurement != null) {
            if (isTemporaryMeasurement) {
              // delete temp measurement
              pmi.AbortStartedMeasurement();
            } else {
              contextManager.DeleteAnnotation(measurement.annotationId);
              selectionManager?.ClearSelection();
            }
            pmi.reportWasmChange();
            notePad.reportWasmChange();
          }
        }}
        data-for='tempMeasureTooltip'
        data-tip={'Discard'}
      />
      {tooltip}
    </OptionIcon>
  );

  const shouldHaveOperatorBar =
    (isTemporaryMeasurement && measurement?.finished);

  const operatorBar = (
    <OptionsGroup atRight>
      {isTemporaryMeasurement && (
        <OptionIcon atRight hide={!measurement || !measurement.finished}>
          <Icon
            name='send'
            size='small'
            viewBox='0 0 40 40'
            onClick={() => {
              const startedMeasurement = pmi?.GetStartedMeasurement();
              if (!startedMeasurement) {
                viewerControl.LogException(new Error(
                  `startedMeasurement is null for itemID: ${itemId} tempKey: ${tempKey}`
                ));
                return;
              }

              const cameraDataObj = startedMeasurement.GetCameraDataJsonObj();

              const msrEnum = viewerControl?.viewer?.module.MeasurementType;
              const { valueStr } = getMeasurementBody(msrEnum, startedMeasurement);
              const measurementBody = valueStr;

              const { startTgt } = startedMeasurement;
              const startTgtObj = startTgt.GetItemTargetJsonObj();

              const supportTargets = [];
              if (startedMeasurement.isFromTo) {
                const { endTgt } = startedMeasurement;
                const endTgtObj = endTgt.GetItemTargetJsonObj();
                const endTgtCameraObj = endTgt.GetCameraDataJsonObj();
                if (endTgtCameraObj) {
                  endTgtObj.camera = endTgtCameraObj;
                }
                supportTargets.push(endTgtObj);
              }

              const tempKey = pmi.FinishMeasurement(itemId);

              const errorHandler = e => {
                Alert.error(
                  'There was a problem sending the measurement, please try again later.',
                );
                notePad.FailFinishedMeasurement(tempKey)
                return contextManager.onSendAnnotationError(e);
              };
              const successHandler = (result: Object) => {
                pmi.reportWasmChange();
                notePad.reportWasmChange();
                Alert.success('Measurement created.');
                return contextManager.onSendAnnotationSuccess(result);
              };

              contextManager.SendAnnotation({
                body: measurementBody,
                tempKey,
                target: startTgtObj,
                camera: cameraDataObj,
                supportTargets: supportTargets,
                kind: 'MEASUREMENT',
                jsonBlob: {
                  "isFromTo": startedMeasurement.isFromTo,
                },
                optionsParameter: {
                  onSuccess: successHandler,
                  onSysErr: errorHandler,
                  onUserErr: errorHandler,
                },
              });
            }}
            data-for='tempMeasureTooltip'
            data-tip='Add'
          />
        </OptionIcon>
      )}
    </OptionsGroup>
  );

  const shouldNotEmpty = !isEmpty;

  const renderFromToElement =
    shouldNotEmpty && pmi.IsFromToMode() && fromToElement;
  const renderDescriptionInfo = descriptionInfo;
  // const renderRotateElement =
  //   shouldNotEmpty && positionOptions && rotateElement;
  // const renderOffsetElement =
  //   shouldNotEmpty && positionOptions && offsetElement;
  const renderOperatorBar = shouldNotEmpty && operatorBar;
  const renderDeleteOrDiscardBtn = shouldNotEmpty && deleteOrDiscardBtn;

  const previewElement = measurement && (
    <MeasureItem measure={measurement} />
  );

  const shouldSelectedDimension = pmi && !pmi.IsFromToMode();

  return {
    isEmpty,
    shouldNotEmpty,
    isFromToModeActive,
    previewElement,
    isActiveDimension,
    operatorBar,
    descriptionInfo,
    // rotateElement,
    // offsetElement,
    deleteOrDiscardBtn,

    renderFromToElement,
    // disableDimension,
    // disableFromSelect,
    handleClickDimension,
    renderOperatorBar,
    renderDescriptionInfo,
    // renderRotateElement,
    // renderOffsetElement,
    handleClickPointToPoint,
    renderDeleteOrDiscardBtn,
    tooltip,
    // stickyOptionGroup,
    emptyContent: isEmpty && noBrepMsg(),
    shouldHaveOperatorBar,
    shouldSelectedDimension,
  };
};
